/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import {
  Box,
  BrickLoader,
  Modal,
  ModalConfirm,
  Spacer,
  theme,
} from "@nordcloud/gnui";
import { LoaderWrap } from "~/components";
import { showError, showSuccess } from "~/services/toast";
import { isEmpty } from "~/tools";
import { useDeleteCustomAction } from "~/views/customActions/hooks/useDeleteCustomAction/useDeleteCustomAction";
import { useGetCustomActionPlans } from "../../hooks/useGetCustomActionsPlans/useGetCustomActionPlans";

type Props = {
  actionId: string;
  isDeleteModalOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
};

const modalLabel = `Delete Custom Action`;
export function DeleteCustomAction({
  actionId,
  isDeleteModalOpen,
  close,
  onSuccess,
}: Props) {
  const [deleteCustomAction] = useDeleteCustomAction({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });

  const { planNames, loading, error } = useGetCustomActionPlans({
    customActionId: actionId,
    skip: !isDeleteModalOpen,
  });

  async function deleteAction(id: string) {
    try {
      await deleteCustomAction(id);
      showSuccess(`Custom Action has been removed.`);
    } catch (e) {
      showError();
    }
  }

  async function onDelete(id: string) {
    await deleteAction(id);
  }

  if (loading || error) {
    return (
      <Modal
        onClose={close}
        isOpen={isDeleteModalOpen}
        contentLabel={modalLabel}
        actions={
          error
            ? [
                {
                  order: 0,
                  onAction: close,
                  label: "Close",
                  severity: "high",
                },
              ]
            : []
        }
      >
        <LoaderWrap loading={loading} Component={BrickLoader}>
          Unable to verify whether this custom action is used in any plans.
          Please try again.
        </LoaderWrap>
      </Modal>
    );
  }

  return (
    <If condition={isEmpty(planNames)}>
      <Then>
        <ModalConfirm
          onClose={close}
          isOpen={isDeleteModalOpen}
          confirm={() => onDelete(actionId)}
          actionLabel="Delete"
          contentLabel={modalLabel}
        >
          {`Are you sure you want to delete Custom Action?`}
        </ModalConfirm>
      </Then>
      <Else>
        <Modal
          onClose={close}
          isOpen={isDeleteModalOpen}
          data-testid="modal-delete-custom-action"
          contentLabel={modalLabel}
          actions={[
            {
              order: 0,
              onAction: close,
              label: "Close",
              severity: "high",
            },
          ]}
        >
          {`You are trying to delete a custom action, but that action is currently being used in the plan(s) below.`}
          <Spacer height={theme.spacing.spacing04} />
          <Box boxStyle="lightGrey">
            <div>
              {planNames.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          </Box>
          <Spacer height={theme.spacing.spacing04} />
          {`If you still want to delete this custom action, you must delete it from the plans first.`}
        </Modal>
      </Else>
    </If>
  );
}
